import { IDetailsRowProps, MessageBarType } from '@fluentui/react';
import {
  debounce,
  defaultPalette,
  detailsHeaderStylesBasic,
  DetailsList,
  detailsListStylesCard,
  EmptyMessage,
  getItemsWithEmptyItemsOfNextPage,
  IColumnWithDataType,
  IDetailsListProps,
  Link,
  linkStylesGhost,
  ListRow,
  Loader,
  loaderStylesSpinnerButton,
  loaderStylesSpinnerXLarge,
  navigate,
  onRenderTagColumn,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  Artifact,
  ArtifactType,
  ArtifactUploadStatus,
  CoreService,
  ListArtifactsRequest,
  ListArtifactsResponse,
  RetrieveArtifactResponse,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { CommonImageSrc, ContentsTitle, defaultPageSize } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { ListCellImage } from '../ListCellImage/ListCellImage';
import { artifactListStylesDefault, IArtifactListClassNames, IArtifactListStyles } from './ArtifactList.styles';

const artifactTypeNameColorMap = new Map<ArtifactType | undefined, { name: string; color: string }>([
  [ArtifactType.ARTIFACT_TYPE_FILE, { name: 'FILE', color: defaultPalette.blue100 }],
  [ArtifactType.ARTIFACT_TYPE_LINK, { name: 'LINK', color: defaultPalette.green100 }],
  [ArtifactType.ARTIFACT_TYPE_UNSPECIFIED, { name: 'UNSPECIFIED', color: defaultPalette.yellow100 }],
]);

const artifactStatusNameColorMap = new Map<ArtifactUploadStatus | undefined, { name: string; color: string }>([
  [ArtifactUploadStatus.ARTIFACT_UPLOAD_STATUS_DONE, { name: 'DONE', color: defaultPalette.green100 }],
  [ArtifactUploadStatus.ARTIFACT_UPLOAD_STATUS_FAILED, { name: 'FAILED', color: defaultPalette.red100 }],
  [ArtifactUploadStatus.ARTIFACT_UPLOAD_STATUS_IN_PROGRESS, { name: 'IN PROGRESS', color: defaultPalette.blue100 }],
  [
    ArtifactUploadStatus.ARTIFACT_UPLOAD_STATUS_NOT_APPLICABLE,
    { name: 'NOT APPLICABLE', color: defaultPalette.yellow100 },
  ],
]);

const urlColumn = {
  key: 'url',
  name: 'URL',
  fieldName: 'url',
};

const fileNameColumn = {
  key: 'filename',
  name: 'File Name',
  fieldName: 'filename',
};

const getColumns = (
  classNames: IArtifactListClassNames,
  onDownload: (e?: MouseEvent, item?: Artifact) => void,
  onDelete?: (item?: Artifact) => void
): IColumnWithDataType[] => {
  const columns: IColumnWithDataType[] = [
    {
      key: 'title',
      name: 'Title',
      fieldName: 'description',
      minWidth: 200,
      data: {
        headerFieldName: 'title',
        listCellProps: {
          emptyMessage: 'No Description',
          onRenderPrefix: (item) =>
            item.title?.toLowerCase().includes('h2o') ? (
              <ListCellImage imageSrc={CommonImageSrc.logo512} />
            ) : (
              <ListCellImage iconName="FormLibraryMirrored" />
            ),
        },
      },
    },
    {
      ...urlColumn,
      minWidth: 200,
    },
    {
      key: 'artifactTypeName',
      name: 'Type',
      fieldName: 'artifactTypeName',
      minWidth: 120,
      onRender: (item: Artifact) =>
        onRenderTagColumn(item, 'Type', 'artifactTypeName', artifactTypeNameColorMap.get(item.artifactType)?.color),
    },
    {
      key: 'uploadStatus',
      name: 'Status',
      fieldName: 'uploadStatus',
      minWidth: 140,
      onRender: (item: Artifact) =>
        onRenderTagColumn(item, 'Status', 'status', artifactStatusNameColorMap.get(item.uploadStatus)?.color),
    },
    {
      key: 'action',
      name: 'Action',
      minWidth: 230,
      onRender: (item: Artifact) => {
        const { artifactType, uploadStatus } = item;
        return (
          <div className={classNames.actions}>
            <div className={classNames.actionButtons}>
              {artifactType === ArtifactType.ARTIFACT_TYPE_LINK ||
              (artifactType !== ArtifactType.ARTIFACT_TYPE_UNSPECIFIED &&
                uploadStatus === ArtifactUploadStatus.ARTIFACT_UPLOAD_STATUS_DONE) ? (
                <Link styles={linkStylesGhost} onClick={(e?: MouseEvent) => onDownload(e, item)}>
                  <div className="download-loader">
                    <Loader styles={loaderStylesSpinnerButton} />
                  </div>
                  {artifactType === ArtifactType.ARTIFACT_TYPE_FILE ? 'Download' : 'Open'}
                </Link>
              ) : null}
              {onDelete && (
                <Link styles={linkStylesGhost} onClick={() => onDelete(item)}>
                  Delete
                </Link>
              )}
            </div>
          </div>
        );
      },
    },
  ];
  return columns;
};

interface ArtifactWithInfo extends Artifact {
  artifactTypeName?: string;
  status?: string;
}

export interface ArtifactListData {
  items: ArtifactWithInfo[];
  pageToken?: string;
  pageSize?: number;
}

const convertArtifactWithInfo = (item: Artifact): ArtifactWithInfo => ({
  ...item,
  artifactTypeName: artifactTypeNameColorMap.get(item.artifactType)?.name,
  status: artifactStatusNameColorMap.get(item.uploadStatus)?.name,
});

interface IArtifactListProps {
  reload?: boolean;
  featureSetId?: string;
  featureSetMajorVersion?: string;
  artifactUploadStatus?: ArtifactUploadStatus;
  canDelete?: boolean;
  onUpdateData?: (count: number, pageSize: number, pageToken: string, name: ContentsTitle) => void;
}

export const ArtifactList = ({
  featureSetId,
  featureSetMajorVersion,
  reload,
  canDelete,
  onUpdateData,
}: IArtifactListProps) => {
  const [data, setData] = useState<ArtifactListData>({
    items: [],
  });
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const refMeta = useRef<{
    isLoadingPage: boolean;
    mounted?: boolean;
  }>({
    isLoadingPage: false,
  });
  const { addToast } = useToast();
  const load = useCallback(
    async ({ items, pageToken }: ArtifactListData = { items: [] }, hasLoading = true) => {
      hasLoading && setLoadingMessage(`Loading artifacts...`);
      try {
        const pageSize = defaultPageSize;
        const req: ListArtifactsRequest = {
          pageSize,
          pageToken,
          featureSetId,
          featureSetMajorVersion: featureSetMajorVersion !== undefined ? parseInt(featureSetMajorVersion) : undefined,
        };
        const { artifacts = [], nextPageToken = '' }: ListArtifactsResponse = await fsApi(
          CoreService.ListArtifacts,
          req
        );
        const newItems = getItemsWithEmptyItemsOfNextPage(items, artifacts, pageSize, convertArtifactWithInfo);
        const newData = {
          items: newItems,
          pageToken: nextPageToken,
          pageSize,
        };
        if (!refMeta.current.mounted) return;
        setData({
          ...newData,
        });
        onUpdateData &&
          onUpdateData(newData.items.length, newData.pageSize, newData.pageToken, ContentsTitle.featureSets);
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading artifacts: `));
      } finally {
        if (!refMeta.current.mounted) return;
        if (hasLoading) setLoadingMessage('');
        refMeta.current.isLoadingPage = false;
      }
    },
    [addToast, onUpdateData]
  );
  const onLoad = useMemo(() => debounce(300, load), [load]);
  const onDownload = useCallback(async (e?: MouseEvent, item?: Artifact) => {
    if (item && e) {
      const el = e.target as HTMLElement;
      el.classList.add('download-loading');
      const { artifactId } = item;
      try {
        const { artifact: { url, artifactType, filename } = {} }: RetrieveArtifactResponse = await fsApi(
          CoreService.RetrieveArtifact,
          { artifactId }
        );
        if (url) {
          if (artifactType === ArtifactType.ARTIFACT_TYPE_LINK) {
            navigate(url);
          } else if (artifactType === ArtifactType.ARTIFACT_TYPE_FILE) {
            navigate(url, true, filename);
          }
        }
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while downloading artifact: `));
      } finally {
        el.classList.remove('download-loading');
      }
    }
  }, []);
  const onDelete = useCallback(
    async (item?: Artifact) => {
      if (item) {
        const { artifactId } = item;
        try {
          await fsApi(CoreService.DeleteArtifact, { artifactId });
          addToast({
            messageBarType: MessageBarType.success,
            message: `Artifact(${artifactId}) has been deleted successfully!`,
          });
          load();
        } catch (err) {
          addToast(getErrorMessageBarMessage(err, `An error has occurred while deleting artifact: `));
        }
      }
    },
    [load]
  );
  const classNames = useClassNames<IArtifactListStyles, IArtifactListClassNames>(
    'ArtifactList',
    artifactListStylesDefault
  );
  const detailsListProps: IDetailsListProps = {
    styles: detailsListStylesCard,
    headerStyles: detailsHeaderStylesBasic,
    isHeaderVisible: false,
    columns: getColumns(classNames, onDownload, canDelete ? onDelete : undefined),
    items: data.items,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      if (item.isNotLoaded && !refMeta.current.isLoadingPage) {
        refMeta.current.isLoadingPage = true;
        onLoad(data, false);
      }
      const newColumns = [...columns];
      if (item.filename) {
        newColumns[1] = { ...newColumns[1], ...fileNameColumn };
      } else {
        newColumns[1] = { ...newColumns[1], ...urlColumn };
      }
      return <ListRow data={item} columns={newColumns} loading={item.isNotLoaded} />;
    },
  };
  useEffect(() => {
    refMeta.current.mounted = true;
    load();
    return () => {
      refMeta.current.mounted = false;
    };
  }, [load]);
  useEffect(() => {
    reload && load();
  }, [reload]);
  return (
    <div className={classNames.root} data-test="artifact-list">
      {loadingMessage ? (
        <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
      ) : (
        <div className={classNames.cardView} data-is-scrollable="true" data-test="artifact-list-card-view">
          {data.items.length === 0 ? (
            <EmptyMessage message="No Artifacts Available" />
          ) : (
            <DetailsList {...detailsListProps} />
          )}
        </div>
      )}
    </div>
  );
};
